import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import _ from 'lodash'

import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Tab,
  Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

import {
  FormProvider,
  RHFFileField,
  RHFMultipleSelectField,
  RHFSelectField,
} from '../../common/hook-form'
import PageTitle from '../../components/PageTitle'
import DownloadSampleFile from '../../components/DownloadSampleFile'
import { apiConstants, postMultipart } from '../../common/constants'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'

// Reuse the analytics-style chrome the BulkDownload pages already use, so the
// admin / courier-config family of pages all read as the same product.
import {
  PageWrapper,
  SectionCard,
  SectionHeader,
  SectionIconBox,
  SectionTitle,
  SectionSubtitle,
  SectionBody,
  RunBtn,
  ResetBtn,
  InfoBanner,
} from '../BulkDownloadLabelsInvoice/styled'

const MODE_OPTIONS = [
  { label: 'Replace — block only these pincodes (clears the existing list)', value: 'REPLACE' },
  { label: 'Add — block these in addition to the existing list', value: 'APPEND' },
  { label: 'Unblock — remove these from the existing blocked list', value: 'REMOVE' },
]

const COURIER_FILTER = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0,
  limit: 5000,
  skipCount: true,
  includes: 'id,name,prozoId,cpAccountCode',
}


/**
 * Bulk Blocked-Pincodes admin page.
 *
 * Lets a Proship admin block (or append to / unblock) the **same** pincode list
 * across many couriers in a single API call. The courier set can come from
 * either route the backend exposes:
 *   • Multi-select tab → sends `prozoIds` form-fields (typical UI usage)
 *   • Upload tab       → sends `prozoIdsFile` (one column CSV)
 *
 * The pincode set is always a CSV upload (one column). Mode picks the atomic
 * Mongo op the backend uses (REPLACE / APPEND / REMOVE).
 */
const BulkBlockedPincodes = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [], isLoading: loadingCouriers } =
    useGetAllCourierPartnersCachedQuery(COURIER_FILTER)

  const [tab, setTab] = useState('fileUpload')
  const [submitting, setSubmitting] = useState(false)
  const [response, setResponse] = useState(null)
  const [showFailures, setShowFailures] = useState(true)
  const [showSkipped, setShowSkipped] = useState(false)

  const courierOptions = useMemo(() => {
    return _.orderBy(
      (couriers || []).filter((c) => c?.prozoId),
      'prozoId'
    ).map((c) => ({
      value: c.prozoId,
      label: c.name ? `${c.prozoId} — ${c.name}` : c.prozoId,
    }))
  }, [couriers])

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        mode: Yup.string().required('Mode is required'),
        pincodesFile: Yup.mixed()
          .required('Pincodes CSV is required')
          .test('non-empty', 'Pincodes CSV is required', (v) => v && v.length > 0),
        // Either multi-select array or uploaded prozoIds CSV — validated based on tab.
        prozoIds: Yup.array().of(Yup.string()),
        prozoIdsFile: Yup.mixed(),
      })
    ),
    defaultValues: {
      mode: 'REPLACE',
      pincodesFile: null,
      prozoIds: [],
      prozoIdsFile: null,
    },
  })
  const { handleSubmit, reset, watch, setValue } = methods
  const selectedProzoIds = watch('prozoIds') || []
  const uploadedProzoIdsFile = watch('prozoIdsFile')

  const handleTabChange = (_e, v) => {
    setTab(v)
    // Clear the other tab's value so we never accidentally send both to the backend.
    if (v === 'multiSelect') setValue('prozoIdsFile', null)
    else setValue('prozoIds', [])
  }

  const handleReset = () => {
    reset()
    setTab('fileUpload')
    setResponse(null)
  }

  const onSubmit = async (data) => {
    // UI-side guard so the user gets a snackbar before we hit the network.
    if (tab === 'multiSelect' && (!data.prozoIds || data.prozoIds.length === 0)) {
      enqueueSnackbar('Please pick at least one courier', { variant: 'warning' })
      return
    }
    if (tab === 'fileUpload' && (!data.prozoIdsFile || data.prozoIdsFile.length === 0)) {
      enqueueSnackbar('Please upload a courier IDs CSV', { variant: 'warning' })
      return
    }

    setSubmitting(true)
    setResponse(null)
    try {
      const fd = new FormData()
      fd.append('pincodes', data.pincodesFile[0])
      if (tab === 'multiSelect') {
        // Send each prozoId as a repeated form-field — Spring binds these to a
        // List<String> @RequestParam on the backend.
        data.prozoIds.forEach((id) => fd.append('prozoIds', id))
      } else {
        fd.append('prozoIdsFile', data.prozoIdsFile[0])
      }
      const url = `${apiConstants.CONFIG_COURIER_UPLOAD_BLOCKED_PINCODES_BULK}?mode=${data.mode}`
      const res = await postMultipart(url, fd)
      setResponse(res?.data)
      const successCount = res?.data?.successes?.length || 0
      const failureCount = res?.data?.failures?.length || 0
      if (failureCount === 0) {
        enqueueSnackbar(
          `Updated ${successCount} courier${successCount === 1 ? '' : 's'} successfully`,
          { variant: 'success' }
        )
      } else {
        enqueueSnackbar(
          `${successCount} updated, ${failureCount} failed — see results below`,
          { variant: 'warning' }
        )
      }
    } catch (err) {
      console.error(err)
      const message = err?.response?.data?.message || err?.message || 'Upload failed'
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageTitle>Bulk Blocked Pincodes</PageTitle>
      <PageWrapper>
        <InfoBanner>
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">
            Block (or unblock) the same set of pincodes across multiple couriers in one go.
            Pick the couriers, upload your pincode list, and choose what to do with each
            courier's existing blocked list.
          </Typography>
        </InfoBanner>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          {/* ── Section 1: Couriers ── */}
          <SectionCard sx={{ mb: 2.5 }}>
            <SectionHeader>
              <SectionIconBox tone="primary">
                <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />
              </SectionIconBox>
              <SectionTitle>1. Choose Couriers</SectionTitle>
              <SectionSubtitle>
                {tab === 'multiSelect'
                  ? `${selectedProzoIds.length} courier${selectedProzoIds.length === 1 ? '' : 's'} selected`
                  : uploadedProzoIdsFile?.[0]?.name || 'No file selected'}
              </SectionSubtitle>
            </SectionHeader>
            <SectionBody>
              <TabContext value={tab}>
                <Box sx={{ borderBottom: '1px solid #eee', mb: 2 }}>
                  <TabList
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                      minHeight: 36,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        minHeight: 36,
                        py: 0.5,
                      },
                    }}
                  >
                    <Tab value="fileUpload" label="Upload a CSV file" />
                    <Tab value="multiSelect" label="Pick from list" />
                  </TabList>
                </Box>
                <TabPanel value="multiSelect" sx={{ p: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <RHFMultipleSelectField
                        name="prozoIds"
                        label={loadingCouriers ? 'Loading couriers…' : 'Couriers'}
                        options={courierOptions}
                        search
                        outputFormat="array"
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    {selectedProzoIds.length > 0 && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedProzoIds.slice(0, 30).map((id) => (
                            <Chip key={id} label={id} size="small" sx={{ height: 22 }} />
                          ))}
                          {selectedProzoIds.length > 30 && (
                            <Chip
                              label={`+${selectedProzoIds.length - 30} more`}
                              size="small"
                              sx={{ height: 22 }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>
                <TabPanel value="fileUpload" sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Upload a CSV with one courier ID per row. Download the sample to see the format.
                    </Typography>
                    <DownloadSampleFile
                      fileName="BLOCK_PINCODE_PROZO_IDS_SAMPLE"
                      label="Download Sample"
                      size="small"
                    />
                  </Box>
                  <RHFFileField name="prozoIdsFile" fileType={['csv']} />
                </TabPanel>
              </TabContext>
            </SectionBody>
          </SectionCard>

          {/* ── Section 2: Pincodes ── */}
          <SectionCard sx={{ mb: 2.5 }}>
            <SectionHeader>
              <SectionIconBox tone="warning">
                <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
              </SectionIconBox>
              <SectionTitle>2. Upload Pincode List</SectionTitle>
              <SectionSubtitle>One 6-digit pincode per row</SectionSubtitle>
            </SectionHeader>
            <SectionBody>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Upload a CSV with one 6-digit pincode per row. Invalid rows will be skipped and reported back without affecting the rest.
                </Typography>
                <DownloadSampleFile
                  fileName="BLOCK_PINCODE_SAMPLE"
                  label="Download Sample"
                  size="small"
                />
              </Box>
              <RHFFileField name="pincodesFile" fileType={['csv']} />
            </SectionBody>
          </SectionCard>

          {/* ── Section 3: Mode ── */}
          <SectionCard sx={{ mb: 2.5 }}>
            <SectionHeader>
              <SectionIconBox tone="primary">
                <TuneIcon sx={{ fontSize: 16 }} />
              </SectionIconBox>
              <SectionTitle>3. What to Do</SectionTitle>
              <SectionSubtitle>Choose how the pincodes apply to each courier</SectionSubtitle>
            </SectionHeader>
            <SectionBody>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <RHFSelectField
                    name="mode"
                    label="Action *"
                    size="small"
                    options={MODE_OPTIONS}
                    width={'100%'}
                  />
                </Grid>
              </Grid>
            </SectionBody>
          </SectionCard>

          {/* ── Actions ── */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
            <RunBtn
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <FileUploadOutlinedIcon fontSize="small" />
                )
              }
            >
              {submitting ? 'Applying…' : 'Apply to Couriers'}
            </RunBtn>
            <ResetBtn
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleReset}
              disabled={submitting}
              startIcon={<RestartAltIcon fontSize="small" />}
            >
              Reset
            </ResetBtn>
          </Box>
        </FormProvider>

        {response && <ResultPanel
          response={response}
          showFailures={showFailures}
          setShowFailures={setShowFailures}
          showSkipped={showSkipped}
          setShowSkipped={setShowSkipped}
        />}
      </PageWrapper>
    </>
  )
}

/** Human-friendly label for the action that was just performed. */
const modeLabel = (mode) => {
  switch (mode) {
    case 'REPLACE': return 'Replaced existing blocked pincodes'
    case 'APPEND':  return 'Added to existing blocked pincodes'
    case 'REMOVE':  return 'Removed pincodes (unblocked)'
    default:        return mode || ''
  }
}

/** Inline result card — summary chips + collapsible lists for failures and skipped rows. */
const ResultPanel = ({ response, showFailures, setShowFailures, showSkipped, setShowSkipped }) => {
  const successCount = response.successes?.length || 0
  const failureCount = response.failures?.length || 0
  const skippedPincodeCount = response.skippedPincodeRows?.length || 0
  const skippedProzoIdCount = response.skippedProzoIdRows?.length || 0

  const allGood = failureCount === 0 && skippedPincodeCount === 0 && skippedProzoIdCount === 0
  const tone = allGood ? 'success' : 'warning'

  return (
    <SectionCard>
      <SectionHeader>
        <SectionIconBox tone={tone}>
          {allGood ? (
            <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />
          ) : (
            <ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </SectionIconBox>
        <SectionTitle>Results</SectionTitle>
        <SectionSubtitle>{modeLabel(response.mode)}</SectionSubtitle>
      </SectionHeader>
      <SectionBody>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            icon={<LocalShippingOutlinedIcon sx={{ fontSize: 14 }} />}
            label={`${response.totalCouriers} courier${response.totalCouriers === 1 ? '' : 's'}`}
            size="small"
            sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
          />
          <Chip
            icon={<LocationOnOutlinedIcon sx={{ fontSize: 14 }} />}
            label={`${response.totalPincodes} pincode${response.totalPincodes === 1 ? '' : 's'}`}
            size="small"
            sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
          />
          <Chip
            icon={<CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />}
            label={`${successCount} updated`}
            size="small"
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
          />
          {failureCount > 0 && (
            <Chip
              icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: 14 }} />}
              label={`${failureCount} failed`}
              size="small"
              sx={{ bgcolor: '#ffebee', color: '#c62828' }}
            />
          )}
          {(skippedPincodeCount + skippedProzoIdCount) > 0 && (
            <Chip
              icon={<ReportProblemOutlinedIcon sx={{ fontSize: 14 }} />}
              label={`${skippedPincodeCount + skippedProzoIdCount} row${(skippedPincodeCount + skippedProzoIdCount) === 1 ? '' : 's'} skipped`}
              size="small"
              sx={{ bgcolor: '#fff4e5', color: '#a04000' }}
            />
          )}
        </Box>

        {failureCount > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                py: 0.5,
              }}
              onClick={() => setShowFailures(!showFailures)}
            >
              <IconButton size="small" sx={{ p: 0.25, mr: 0.5 }}>
                {showFailures ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2" fontWeight={600} color="#c62828">
                Couriers that failed ({failureCount})
              </Typography>
            </Box>
            <Collapse in={showFailures}>
              <Box
                sx={{
                  border: '1px solid #ffcdd2',
                  borderRadius: 1,
                  bgcolor: '#fff5f5',
                  p: 1,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {response.failures.map((f, i) => (
                  <Typography key={i} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    {f.prozoId} — <span style={{ color: '#c62828' }}>{f.reason}</span>
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {(skippedPincodeCount + skippedProzoIdCount) > 0 && (
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5 }}
              onClick={() => setShowSkipped(!showSkipped)}
            >
              <IconButton size="small" sx={{ p: 0.25, mr: 0.5 }}>
                {showSkipped ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2" fontWeight={600} color="#a04000">
                Skipped rows in your CSV ({skippedPincodeCount + skippedProzoIdCount})
              </Typography>
            </Box>
            <Collapse in={showSkipped}>
              <Box
                sx={{
                  border: '1px solid #ffcc80',
                  borderRadius: 1,
                  bgcolor: '#fffaf0',
                  p: 1,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {skippedPincodeCount > 0 && (
                  <>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      Pincodes file — invalid rows
                    </Typography>
                    {response.skippedPincodeRows.map((r, i) => (
                      <Typography
                        key={`p-${i}`}
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        row {r.row}: "{r.value}" — {r.reason}
                      </Typography>
                    ))}
                  </>
                )}
                {skippedProzoIdCount > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ display: 'block', mb: 0.5, mt: skippedPincodeCount > 0 ? 1 : 0 }}
                    >
                      Couriers file — invalid rows
                    </Typography>
                    {response.skippedProzoIdRows.map((r, i) => (
                      <Typography
                        key={`i-${i}`}
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        row {r.row}: "{r.value}" — {r.reason}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </SectionBody>
    </SectionCard>
  )
}

export default BulkBlockedPincodes
